import React from 'react'
import { Box, Button, Card, CardContent, Grid2, Skeleton, Stack, SvgIcon, Typography } from '@mui/material'
import ShoppingCartIcon from '@heroicons/react/24/outline/ShoppingCartIcon';
import HandThumbUpIcon from '@heroicons/react/24/outline/HandThumbUpIcon';
import TruckIcon from '@heroicons/react/24/outline/TruckIcon';
import AdjustmentsVerticalIcon from '@heroicons/react/24/outline/AdjustmentsVerticalIcon';
import ArrowUturnLeftIcon from '@heroicons/react/24/outline/ArrowUturnLeftIcon';
import DocumentCheckIcon from '@heroicons/react/24/outline/DocumentCheckIcon';
import PhoneIcon from '@heroicons/react/24/outline/PhoneIcon';
import CogIcon from '@heroicons/react/24/outline/CogIcon';
import XMarkIcon from '@heroicons/react/24/outline/XMarkIcon';
import DocumentDuplicateIcon from '@heroicons/react/24/outline/DocumentDuplicateIcon';
import CalendarIcon from '@heroicons/react/24/outline/CalendarIcon'
import ChevronDownIcon from '@heroicons/react/24/outline/ChevronDownIcon'
import { useNavigate } from 'react-router-dom';
import { authPostRequest, getRequest, postRequest } from '../../services/api-service';
import { getAllUsersByRoleUrl, getAllVendorsUrl, getExternalProductsOrdersStatisticsUrl, getExternalProductsOrdersTopRegionsUrl, getProductsOrdersStatisticsUrl, getProductsOrdersTopRegionsUrl } from '../../seed/url';
import CustomTabs from '../../dashboard/components/CustomTabs';
import {
  formatMoney,
  getPercenteage,
  ordersBasedOnVendors,
  SMAIADMIN,
  SMAICASHIER,
  SMAIDISPATCHMANAGER,
  SMAIINVENTORYMANAGER,
  SMAISECRETARY,
  SMAISUPERVISOR,
  VENDOR,
} from '../../utils/constant';
import dayjs from 'dayjs';
import { CustomPopOver } from '../../components/custom-popover';
import { usePopover } from '../../hooks/use-popover';
import TopRegionsBarChart from '../VendorHome/TopRegionsBarChart';
import StatCard from '../../dashboard/components/StatCard';
import { OrdersPieChart } from '../VendorHome/OrdersPieChart';
import MobileStatCard, { MobileStatCardSkeleton } from '../../dashboard/mobile/MobileStatCard';
import { UserFilterAutocomplete } from '../../components/user-filter-autocomplete';
import { ResponsiveFilterBar } from '../../components/responsive-filter-bar';
import { useIsMobile } from '../../hooks/use-is-mobile';

const STAFF_ROLES = [
  SMAISECRETARY,
  SMAISUPERVISOR,
  SMAICASHIER,
  SMAIADMIN,
  SMAIINVENTORYMANAGER,
  SMAIDISPATCHMANAGER,
  VENDOR,
];

const getUserDisplayName = (user) => {
  const name = `${user.firstName || ''} ${user.secondName || ''}`.trim();
  return name || user.userName || `User #${user.id}`;
};

const buildDashboardStats = (data, currentTab, navigate) => {
  const isOfflineOrders = currentTab === ordersBasedOnVendors[1].value;
  const ordersPath = isOfflineOrders ? '/orders/imported-orders' : '/orders/orders';

  const stats = [
    {
      name: "Total Orders",
      value: data?.total_orders,
      percentage: getPercenteage(data?.total_orders, data?.total_orders),
      productCost: data?.total_orders_product_cost,
      shippingCost: data?.total_orders_shipping_cost,
      icon: <ShoppingCartIcon />,
      onClick: () => {
        navigate(ordersPath);
      }
    },
    {
      name: "Pending Orders",
      value: data?.pending_orders,
      percentage: getPercenteage(data?.pending_orders, data?.total_orders),
      productCost: data?.pending_orders_product_cost,
      shippingCost: data?.pending_orders_shipping_cost,
      icon: <AdjustmentsVerticalIcon />,
      onClick: () => {
        navigate(ordersPath);
      }
    },
    {
      name: "Call Again Orders",
      value: data?.need_to_call_orders,
      percentage: getPercenteage(data?.need_to_call_orders, data?.total_orders),
      productCost: data?.need_to_call_orders_product_cost,
      shippingCost: data?.need_to_call_orders_shipping_cost,
      icon: <PhoneIcon />,
      onClick: () => {
        navigate(ordersPath);
      }
    },
    {
      name: "Confirmed Orders",
      value: data?.confirmed_orders,
      percentage: getPercenteage(data?.confirmed_orders, data?.total_orders),
      productCost: data?.confirmed_orders_product_cost,
      shippingCost: data?.confirmed_orders_shipping_cost,
      icon: <HandThumbUpIcon />,
      onClick: () => {
        navigate(ordersPath);
      }
    },
    {
      name: "Processing Orders",
      value: data?.processing_orders,
      percentage: getPercenteage(data?.processing_orders, data?.total_orders),
      productCost: data?.processing_orders_product_cost,
      shippingCost: data?.processing_orders_shipping_cost,
      icon: <CogIcon />,
      onClick: () => {
        navigate(ordersPath);
      }
    },
    {
      name: "Intransit Orders",
      value: data?.in_transit_orders,
      percentage: getPercenteage(data?.in_transit_orders, data?.total_orders),
      productCost: data?.in_transit_orders_product_cost,
      shippingCost: data?.in_transit_orders_shipping_cost,
      icon: <TruckIcon />,
      onClick: () => {
        navigate(ordersPath);
      }
    },
    {
      name: "Delivered Orders",
      value: data?.delivered_orders,
      percentage: getPercenteage(data?.delivered_orders, data?.confirmed_orders),
      productCost: data?.delivered_orders_product_cost,
      shippingCost: data?.delivered_orders_shipping_cost,
      icon: <DocumentCheckIcon />,
      onClick: () => {
        navigate(ordersPath);
      }
    },
    {
      name: "Returned Orders",
      value: data?.returned_orders,
      percentage: getPercenteage(data?.returned_orders, data?.confirmed_orders),
      productCost: data?.returned_orders_product_cost,
      shippingCost: data?.returned_orders_shipping_cost,
      icon: <ArrowUturnLeftIcon />,
      onClick: () => {
        navigate(ordersPath);
      }
    },
    {
      name: "Cancelled Orders",
      value: data?.cancelled_orders,
      percentage: getPercenteage(data?.cancelled_orders, data?.total_orders),
      productCost: data?.cancelled_orders_product_cost,
      shippingCost: data?.cancelled_orders_shipping_cost,
      icon: <XMarkIcon />,
      onClick: () => {
        navigate(ordersPath);
      }
    },
  ];

  if (isOfflineOrders) {
    stats.push({
      name: "Duplicated Orders",
      value: data?.duplicated_orders,
      percentage: getPercenteage(data?.duplicated_orders, data?.total_orders),
      productCost: data?.duplicated_orders_product_cost,
      shippingCost: data?.duplicated_orders_shipping_cost,
      icon: <DocumentDuplicateIcon />,
      onClick: () => {
        navigate('/orders/imported-orders');
      }
    });
  }

  return stats;
};

function Home() {
  const popOver = usePopover();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [activeVendor, setActiveVendor] = React.useState(0);
  const [activeUserId, setActiveUserId] = React.useState(0);
  const [isLoadingVendor, setIsLoadingVendor] = React.useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = React.useState(true);
  const [vendors, setVendors] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true)
  const [dashboardData, setDashboardData] = React.useState([]);
  const [dashboardDataObject, setDashboardDataObject] = React.useState({});
  const [topRegions, setTopRegions] = React.useState([]);
  const [isLoadingTopRegions, setIsLoadingTopRegions] = React.useState(true);
  const [currentTab, setCurrentTab] = React.useState(ordersBasedOnVendors[0].value);
  const [body, setBody] = React.useState({
    "from": dayjs().startOf('day'),
    "to": dayjs(),
  });

  const handleBodyChange = (newValue, key) => {
    setBody({ ...body, [key]: newValue, })
  }

  const getProductsOrdersStatistics = React.useCallback(
    () => {
      setIsLoading(true)
      postRequest(
        currentTab === ordersBasedOnVendors[0].value ?
          getProductsOrdersStatisticsUrl :
          getExternalProductsOrdersStatisticsUrl,
        {
          from: body.from.format('YYYY-MM-DD HH:mm:ss.SSS'),
          to: body.to.format('YYYY-MM-DD HH:mm:ss.SSS'),
          vendor_id: activeVendor,
          user_id: activeUserId,
        },
        (data) => {
          setDashboardDataObject(data);
          setDashboardData(buildDashboardStats(data, currentTab, navigate));
          setIsLoading(false)
        },
        (error) => {
          setIsLoading(false)
        },
      )
    },
    [currentTab, body.from, body.to, activeVendor, activeUserId, navigate]
  )

  const getProductsOrdersTopRegions = React.useCallback(
    () => {
      getRequest(
        currentTab === ordersBasedOnVendors[0].value ?
          getProductsOrdersTopRegionsUrl :
          getExternalProductsOrdersTopRegionsUrl,
        (data) => {
          setTopRegions(data);
          setIsLoadingTopRegions(false);
        },
        (error) => {
          setIsLoadingTopRegions(false);
        },
      )
    }, [currentTab])

  const fetchUsersByRole = React.useCallback(
    (role) =>
      new Promise((resolve) => {
        authPostRequest(
          getAllUsersByRoleUrl,
          {
            role,
            sort: 'id desc',
            limit: 100,
            page: 1,
          },
          (data) => resolve(data?.results || []),
          () => resolve([]),
        );
      }),
    [],
  );

  const getAllStaffUsers = React.useCallback(async () => {
    setIsLoadingUsers(true);
    const results = await Promise.all(STAFF_ROLES.map(fetchUsersByRole));
    const uniqueUsers = Array.from(
      new Map(results.flat().map((user) => [user.id, user])).values(),
    ).sort((a, b) => getUserDisplayName(a).localeCompare(getUserDisplayName(b)));

    setUsers(
      uniqueUsers.map((user) => ({
        ...user,
        userName: getUserDisplayName(user),
      })),
    );
    setIsLoadingUsers(false);
  }, [fetchUsersByRole]);

  const getAllVendors = React.useCallback(async () => {
    setIsLoadingVendor(true);
    authPostRequest(
      currentTab === ordersBasedOnVendors[0].value ?
        getAllVendorsUrl :
        getAllUsersByRoleUrl,
      {
        role: "smai_vendor",
        sort: "id desc",
        limit: 100,
        page: 1
      },
      (data) => {
        setVendors(
          data.results.map((item) => ({
            ...item,
            userName: getUserDisplayName(item),
          })),
        );
        setIsLoadingVendor(false);
      },
      (error) => {
        setIsLoadingVendor(false);
      }
    );
  }, [currentTab]);

  React.useEffect(() => {
    getProductsOrdersStatistics()
  }, [getProductsOrdersStatistics]);

  React.useEffect(() => {
    getProductsOrdersTopRegions()
  }, [getProductsOrdersTopRegions]);

  React.useEffect(() => {
    getAllVendors();
  }, [getAllVendors]);

  React.useEffect(() => {
    getAllStaffUsers();
  }, [getAllStaffUsers]);

  const vendorFilter = (
    <UserFilterAutocomplete
      users={vendors}
      isLoadingUsers={isLoadingVendor}
      onChange={setActiveVendor}
      label="Vendor Name"
      id="vendors-list"
    />
  );

  const userFilter = (
    <UserFilterAutocomplete
      users={users}
      isLoadingUsers={isLoadingUsers}
      onChange={setActiveUserId}
    />
  );

  return (
    <>
      <CustomPopOver
        id={popOver.id}
        anchorEl={popOver.anchorRef}
        open={popOver.open}
        onClose={popOver.handleClose}
        showDates={true}
        from={body.from}
        to={body.to}
        handleBodyChange={handleBodyChange}
      />
      <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
        {isMobile ? (
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <CustomTabs
              tabs={ordersBasedOnVendors}
              currentTab={currentTab}
              onClick={(item) => {
                setCurrentTab(item.value);
              }}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                onClick={(event) => popOver.handleOpen(event)}
                startIcon={
                  <SvgIcon fontSize='small'>
                    <CalendarIcon />
                  </SvgIcon>
                }
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: 40,
                  borderRadius: 999,
                  bgcolor: 'background.paper',
                  justifyContent: 'flex-start',
                }}
              >
                <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                  {`${body.from.format('D MMM, HH:mm')} – ${body.to.format('D MMM, HH:mm')}`}
                </Typography>
              </Button>
              <ResponsiveFilterBar activeCount={(activeVendor ? 1 : 0) + (activeUserId ? 1 : 0)}>
                {vendorFilter}
                {userFilter}
              </ResponsiveFilterBar>
            </Stack>
          </Stack>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                mb: 3,
                gap: 2,
              }}
            >
              <Typography sx={{ mr: "auto" }} component="h2" variant="h6">Overview</Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: 1,
                  borderColor: '#bfbfbf',
                  borderRadius: 1,
                  px: 1,
                  py: 0.8
                }}
              >
                <Button
                  sx={{
                    color: "grey"
                  }}
                  variant='text'
                  startIcon={
                    <SvgIcon fontSize='small'>
                      <CalendarIcon />
                    </SvgIcon>
                  }
                  endIcon={
                    <SvgIcon fontSize='small'>
                      <ChevronDownIcon />
                    </SvgIcon>
                  }
                  onClick={(event) => {
                    popOver.handleOpen(event)
                  }}
                >
                  {`${body.from.format('MMMM D, YYYY HH:mm:ss')} - `}
                  {`${body.to.format('MMMM D, YYYY HH:mm:ss')}`}
                </Button>
              </Box>
              {vendorFilter}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                mb: 1.5,
              }}
            >
              <CustomTabs
                tabs={ordersBasedOnVendors}
                currentTab={currentTab}
                onClick={(item) => {
                  setCurrentTab(item.value);
                }}
              />
              <Box sx={{ ml: { xs: 0, sm: 'auto' } }}>
                {userFilter}
              </Box>
            </Box>
          </>
        )}
        {isLoading === true &&
          <Grid2
            container
            spacing={isMobile ? 1.25 : 2}
            columns={12}
            sx={{ mb: (theme) => theme.spacing(2) }}
          >
            {Array.from({
              length: currentTab === ordersBasedOnVendors[1].value ? 10 : 9,
            }, (_, i) => i + 1).map((_, index) => {
              return (
                <Grid2 key={index} size={{ xs: 6, sm: 6, lg: 3 }}>
                  {isMobile ?
                    <MobileStatCardSkeleton /> :
                    <Skeleton variant='rectangular' height={130} />
                  }
                </Grid2>
              );
            })}
          </Grid2>
        }
        {!isLoading &&
          <Grid2
            container
            spacing={isMobile ? 1.25 : 2}
            columns={12}
            sx={{ mb: (theme) => theme.spacing(2) }}
          >
            {dashboardData
              .map((data, index) => {

                return (
                  <Grid2 key={index} size={{ xs: 6, sm: 6, lg: 3 }}>
                    {isMobile ?
                      <MobileStatCard
                        title={data.name}
                        value={data.value}
                        icon={data.icon}
                        percentage={data.percentage}
                        productCost={data.productCost}
                        shippingCost={data.shippingCost}
                        onClick={data.onClick}
                      /> :
                      <StatCard
                        title={data.name}
                        value={data.value}
                        interval={`${formatMoney(data.productCost)}, ${formatMoney(data.shippingCost)}`}
                        percentage={data.percentage}
                        isLoading={isLoading}
                      />
                    }
                  </Grid2>
                )
              })
            }
          </Grid2>
        }
        <Grid2
          container
          spacing={2}
          columns={12}
          sx={{ mb: (theme) => theme.spacing(2) }}
        >
          <Grid2 size={{ xs: 12, md: 12, lg: 9 }}>
            <Card variant="outlined" sx={{ height: '100%', flexGrow: 1 }}>
              <CardContent>
                <Typography variant="h5">Top Regions</Typography>
                <TopRegionsBarChart
                  topRegions={topRegions}
                  isLoadingTopRegions={isLoadingTopRegions}
                />
              </CardContent>
            </Card>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 12, lg: 3 }}>
            <Card variant="outlined" sx={{ height: '100%', flexGrow: 1 }}>
              <CardContent>
                <OrdersPieChart
                  chartSeries={[
                    dashboardDataObject?.delivered_orders || 0,
                    dashboardDataObject?.returned_orders || 0,
                    dashboardDataObject?.cancelled_orders || 0
                  ]}
                  labels={['Delivered', 'Returned', 'Cancelled']}
                  deliveryRates={[
                    {
                      value: getPercenteage(dashboardDataObject?.delivered_orders || 0, dashboardDataObject?.total_orders || 0),
                      label: "Total Delivery Rate"
                    },
                    {
                      value: getPercenteage(dashboardDataObject?.delivered_orders || 0, dashboardDataObject?.confirmed_orders || 0),
                      label: "Confirmed Delivery Rate"
                    },
                    {
                      value: getPercenteage(dashboardDataObject?.returned_orders || 0, dashboardDataObject?.confirmed_orders || 0),
                      label: "Return Rate"
                    },
                    {
                      value: getPercenteage(dashboardDataObject?.confirmed_orders || 0, dashboardDataObject?.total_orders || 0),
                      label: "Confirmation Rate"
                    }
                  ]}
                  isLoading={isLoading}
                />
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>
      </Box>
    </>
  )
}

export default Home